import './index.scss';
import ModalBase from '.';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle } from '../../helpers/utils';
import ModalExecuteAction from './ModalExecuteAction';
import useUSDRequest from '../../hooks/useUSDRequest';
import {
  NIGERIAN_STATES,
  USD_EMPLOYMENT_STATUS_OPTIONS,
  USD_IDENTITY_TYPE_OPTIONS,
  USD_PRIMARY_PURPOSE_OPTIONS,
  USD_SOURCE_OF_FUNDS_OPTIONS,
} from '../../constants';
import { ModalEditUSDRequestDetailsProps, USDEditableField, USDEditForm } from '../../types';

const EDITABLE_FIELDS: USDEditableField[] = [
  'identity_type',
  'identity_number',
  'identity_country',
  'identity_issue_date',
  'identity_expiry_date',
  'address_street_number',
  'address_street_name',
  'address_state',
  'address_postal_code',
  'address_country',
  'employment_status',
  'occupation',
  'primary_purpose',
  'source_of_funds',
  'expected_monthly_inflow',
];

const DATE_FIELDS: USDEditableField[] = ['identity_issue_date', 'identity_expiry_date'];

const INPUT_DATE = 'YYYY-MM-DD'; // format <input type="date"> uses

const toFormValue = (field: USDEditableField, value: any): string => {
  if (value === null || value === undefined) return '';
  if (DATE_FIELDS.includes(field)) {
    const date = dayjs(String(value));
    return date.isValid() ? date.format(INPUT_DATE) : '';
  }
  return String(value);
};

const buildForm = (request: any): USDEditForm =>
  EDITABLE_FIELDS.reduce((acc, field) => {
    acc[field] = toFormValue(field, request?.[field]);
    return acc;
  }, {} as USDEditForm);

// The stored value is not always one of our known options (the provider can
// send a slug we have not catalogued). Keeping it in the list means opening the
// modal never silently blanks a field the admin did not intend to change.
const withCurrentValue = (options: Array<{ label: string; value: string }>, value: string) =>
  !value || options.some((option) => option.value === value) ? options : [...options, { label: value, value }];

function ModalEditUSDRequestDetails({ modalIsOpen, setIsOpen, request }: ModalEditUSDRequestDetailsProps) {
  const { updateUSDDocumentFields, isUpdatingDocument } = useUSDRequest();
  const [form, setForm] = useState<USDEditForm>(() => buildForm(request));
  const [initialForm, setInitialForm] = useState<USDEditForm>(() => buildForm(request));
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (!modalIsOpen) {
      setShowConfirmation(false);
      return;
    }
    const populated = buildForm(request);
    setForm(populated);
    setInitialForm(populated);
  }, [modalIsOpen, request]);

  const selectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const handleChange = (field: USDEditableField) => (value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  // Only the touched fields are sent — the endpoint treats everything but
  // docId/userId as optional.
  const changes = useMemo(() => {
    return EDITABLE_FIELDS.reduce(
      (acc, field) => {
        if (form[field].trim() !== initialForm[field].trim()) acc[field] = form[field].trim();
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [form, initialForm]);

  const closeModal = () => {
    if (isUpdatingDocument) return;
    setIsOpen(false);
  };

  // Hands over to the password confirmation step; the form stays mounted so a
  // cancelled confirmation drops the admin back on their unsaved edits.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!Object.keys(changes).length) {
      Alert.info('No changes were made');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmUpdate = () => {
    updateUSDDocumentFields(changes, () => {
      setShowConfirmation(false);
      setIsOpen(false);
    });
  };

  return (
    <>
      <ModalBase modalIsOpen={modalIsOpen && !showConfirmation} setIsOpen={setIsOpen}>
        <div className="modal-processor modal-processor--sm-title">
          <div className="modal-processor__header">
            <h2>Edit Request Details</h2>
            <button type="button" onClick={closeModal}>
              &times;
            </button>
          </div>

          <form className="modal-processor__body modal-processor__body--scroll" onSubmit={handleSubmit}>
            <h3 className="modal-processor__section-title">Identity</h3>

            <Select
              {...selectStyle}
              labelStyle={{ color: '#666766' }}
              label="ID Type"
              placeholder="Select ID type"
              options={withCurrentValue(USD_IDENTITY_TYPE_OPTIONS, form.identity_type)}
              value={form.identity_type}
              onChange={handleChange('identity_type')}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="ID Number"
              placeholder="Enter ID number"
              value={form.identity_number}
              onChange={(e) => handleChange('identity_number')(e.target.value)}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Issued Country"
              placeholder="e.g. NG"
              value={form.identity_country}
              onChange={(e) => handleChange('identity_country')(e.target.value)}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Issue Date"
              type="date"
              value={form.identity_issue_date}
              onChange={(e) => handleChange('identity_issue_date')(e.target.value)}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Expiry Date"
              type="date"
              value={form.identity_expiry_date}
              onChange={(e) => handleChange('identity_expiry_date')(e.target.value)}
            />

            <h3 className="modal-processor__section-title">Address</h3>

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Street Number"
              placeholder="e.g. 12"
              value={form.address_street_number}
              onChange={(e) => handleChange('address_street_number')(e.target.value)}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Street Name"
              placeholder="e.g. Admiralty Way"
              value={form.address_street_name}
              onChange={(e) => handleChange('address_street_name')(e.target.value)}
            />

            <Select
              {...selectStyle}
              labelStyle={{ color: '#666766' }}
              label="State"
              placeholder="Select state"
              options={withCurrentValue(NIGERIAN_STATES, form.address_state)}
              value={form.address_state}
              onChange={handleChange('address_state')}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Postal Code"
              placeholder="e.g. 101233"
              value={form.address_postal_code}
              onChange={(e) => handleChange('address_postal_code')(e.target.value)}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Country"
              placeholder="e.g. NG"
              value={form.address_country}
              onChange={(e) => handleChange('address_country')(e.target.value)}
            />

            <h3 className="modal-processor__section-title">Financial Information</h3>

            <Select
              {...selectStyle}
              labelStyle={{ color: '#666766' }}
              label="Employment Status"
              placeholder="Select employment status"
              options={withCurrentValue(USD_EMPLOYMENT_STATUS_OPTIONS, form.employment_status)}
              value={form.employment_status}
              onChange={handleChange('employment_status')}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Occupation"
              placeholder="e.g. Engineer"
              value={form.occupation}
              onChange={(e) => handleChange('occupation')(e.target.value)}
            />

            <Select
              {...selectStyle}
              labelStyle={{ color: '#666766' }}
              label="Primary Purpose"
              placeholder="Select primary purpose"
              options={withCurrentValue(USD_PRIMARY_PURPOSE_OPTIONS, form.primary_purpose)}
              value={form.primary_purpose}
              onChange={handleChange('primary_purpose')}
            />

            <Select
              {...selectStyle}
              labelStyle={{ color: '#666766' }}
              label="Source of Funds"
              placeholder="Select source of funds"
              options={withCurrentValue(USD_SOURCE_OF_FUNDS_OPTIONS, form.source_of_funds)}
              value={form.source_of_funds}
              onChange={handleChange('source_of_funds')}
            />

            <Input
              {...OverideInputStyle}
              labelStyle={{ color: '#666766' }}
              label="Expected Monthly Inflow"
              placeholder="e.g. 500000"
              value={form.expected_monthly_inflow}
              onChange={(e) => handleChange('expected_monthly_inflow')(e.target.value.replace(/[^\d.]/g, ''))}
            />

            <p className="modal-processor__sub-text modal-processor__change-count">
              {Object.keys(changes).length
                ? `${Object.keys(changes).length} field${Object.keys(changes).length > 1 ? 's' : ''} will be updated`
                : 'Edit a field to enable saving'}
            </p>

            <div className="modal-processor__actions">
              <button type="button" onClick={closeModal} className="modal-processor__actions-reject">
                Cancel
              </button>
              <Button
                {...OverideButtonStyle}
                className="modal-processor__actions-resolve"
                type="submit"
                disabled={isUpdatingDocument || !Object.keys(changes).length}
              >
                {isUpdatingDocument ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </ModalBase>

      <ModalExecuteAction
        modalIsOpen={showConfirmation}
        setIsOpen={setShowConfirmation}
        text="save these changes to this request?"
        action={confirmUpdate}
        actionColor="#1734CA"
        isLoading={isUpdatingDocument}
      />
    </>
  );
}

export default ModalEditUSDRequestDetails;
